// sm is for the glia js sdk integration
// Should be first to avoid dependency issues
import "es6-promise/auto";
import "core-js/stable";
import "regenerator-runtime/runtime";

import "@narmi/design_system/dist/style.css";
import "cerulean/styles.scss";
import "./azul.scss";

import React from "react";
import ReactDOM from "react-dom/client";
import { initReactI18next } from "react-i18next";
import i18next from "i18next";

import { withNotifications } from "cerulean";
import OrganizationUser from "byzantine/src/OrganizationUser";
import type { InstitutionFeatures } from "byzantine/src/types";
import User from "byzantine/src/User";
import Account from "byzantine/src/Account";
import Membership from "byzantine/src/Membership";
import { SupportContact } from "byzantine/src/types/models/v1/Support.types";
import {
  SupportFaqCategory,
  SupportLink,
} from "byzantine/src/types/SettingLocalizers.types";
import App from "byzantine/src/App";
import { DepositInsurer } from "byzantine/src/types";
import { FingerprintJSPro } from "@fingerprintjs/fingerprintjs-pro-react";
import SignedUrlIframe from "./components/SignedUrlIframe";
import { preloadState } from "./src";

import AppDownloadDialog from "./components/AppDownloadDialog";
import DownloadTransactionsDialog from "./components/DownloadTransactionsDialog";
import AppProviders from "./components/AppProviders";
import EstatementsManager from "./components/estatements/EstatementsManager";
import TransferPageContainer from "./components/transfer/TransferPageContainer";
import AccountPageContainer from "./components/accounts/AccountPageContainer";
import ReferAMember from "./components/refer_a_member/ReferAMember";
import BeneficiariesContainer from "./components/beneficiaries/BeneficiariesContainer";
import LoginPageContainer from "./components/authentication/LoginPageContainer";
import ChangePasswordContainer from "./components/authentication/ChangePasswordContainer";
import PasswordResetPageContainer from "./components/authentication/PasswordResetPageContainer";
import MfaDeviceSelect from "./components/authentication/MfaDeviceSelect";
import MfaCodeEnter from "./components/authentication/MfaCodeEnter";
import EnrollmentPageContainer from "./components/enrollments/EnrollmentPageContainer";
import InactivityDialog from "./components/InactivityDialog";
import AutomaticSavingsPageContainer from "./components/automatic_savings/AutomaticSavingsPageContainer";

import OrganizationUsersList from "./components/organization/OrganizationUsers";
import OrganizationNewUser from "./components/organization/OrganizationNewUser";
import OrganizationEditUser from "./components/organization/OrganizationEditUser";
import DualApprovalContainer from "./components/DualApproval/DualApprovalContainer";
import DualApprovalNotificationBar from "./components/DualApproval/DualApprovalNotificationBar";
import SupportPageLayout from "./components/support_page/SupportPageLayout";
import MessageCenterLayout from "./components/message_center/MessageCenterLayout";

import AppAuthorize from "./components/AppAuthorize";
import Main from "./components/Main";
import InstitutionAnnouncement from "./components/InstitutionAnnouncement";
import OAuth2Authorize from "./components/OAuth2Authorize";
import OAuth2AuthorizeError from "./components/OAuth2AuthorizeError";
import SettingsNavBarContainer from "./components/settings/SettingsNavBar";

import DocumentsPageContainer from "./src/modules/documents/DocumentsPageContainer/DocumentsPageContainer";
import TransactionExportPageContainer from "./src/modules/reports/TransactionExportPageContainer/TransactionExportPageContainer";
import ServicePageContainer from "./components/service_page/ServicePageContainer";

import DisclosuresReconsentDialog from "./components/DisclosuresReconsentDialog";
import type { LegacyNafUrl, Institution } from "./types";

/* Security page */
import Footer from "./components/Footer";

import AddCardNafApp from "./components/transfer/loans/payment_by_card/add_a_card/AddCardNafApp";
import createLocationChangeEvent from "./src/utils/createLocationChangeEvent";
import Sidebar from "./src/modules/sidebar/components/Sidebar";
import SidebarTop from "./src/modules/sidebar/SidebarTop";
import PositivePay from "./components/positivePay/component";
import ZEscrow from "./components/zescrow/component";
import MobileEmulation from "./src/modules/mobile/emulation";

declare global {
  interface Window {
    azulI18nCatalog: { [key: string]: string };
    sm: any;
    isFetchingFtls: boolean;
  }
}

i18next
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    lng: "en",
    nsSeparator: false,
    keySeparator: false,
    resources: {
      en: { translation: window?.azulI18nCatalog || {} },
    },
  });

const initialiseGlia = ({
  full_name: name,
  email,
  core_user_id: externalId,
}: {
  full_name: string;
  email: string;
  core_user_id: string;
}) => {
  if (typeof window?.sm === "undefined") {
    // for slower internet connections, sm can be undefined when initialiseGlia is called
    setTimeout(initialiseGlia, 1000, {
      full_name: name,
      email,
      core_user_id: externalId,
    });
    return;
  }

  window.sm
    .getApi({ version: "v1" })
    .then(
      (salemove: {
        updateInformation: ({
          name,
          email,
          externalId,
        }: {
          name: string;
          email: string;
          externalId: string;
        }) => void;
      }) =>
        salemove.updateInformation({
          name,
          email,
          externalId,
        })
    );
};

// Microfrontend localization handling...
//
// The first useFluentLocalization hook that runs
// will read this and flip it to `true` while fetching FTLs.
//
// Hooks that run _after_ this is set to `true` will instead
// subscribe to a CustomEvent to get the loaded `ReactLocalization`
window.isFetchingFtls = false;

interface DataToPreload {
  user?: API.User;
  accounts?: API.AnyAccount[];
  limits?: any;
  bankingMenus?: Record<string, unknown>;
  institutionApps?: unknown[];
}

document.addEventListener("DOMContentLoaded", () => {
  createLocationChangeEvent();

  const dataToPreload: DataToPreload = {};

  let institution: Institution = {
    name: "",
    internalName: "",
    type: "",
    primaryColor: "",
    s3ImagesBucket: "",
    secondary_color: "",
    tertiary_color: "",
    depositInsurer: DepositInsurer.NONE,
    logo: "",
    apps: [],
    servicesApps: [],
    fingerprintApiKey: "",
    fingerprintProxyDomain: "",
  };
  const rawInstitutionApps: unknown[] = JSON.parse(
    document.getElementById("institution_apps")?.textContent || "[]"
  );
  const rawInstitutionServicesApps: unknown[] = JSON.parse(
    document.getElementById("institution_services_apps")?.textContent || "[]"
  );
  if (document.getElementById("institution_long_name")) {
    institution = {
      name: JSON.parse(
        document.getElementById("institution_long_name")?.textContent || ""
      ),
      internalName: JSON.parse(
        document.getElementById("institution_internal_name")?.textContent || ""
      ),
      type: JSON.parse(
        document.getElementById("institution_type")?.textContent || ""
      ),
      primaryColor: JSON.parse(
        document.getElementById("institution_primary_color")?.textContent || ""
      ),
      s3ImagesBucket:
        JSON.parse(
          document.getElementById("s3_images_bucket")?.textContent || ""
        ) || "",
      secondary_color: JSON.parse(
        document.getElementById("institution_secondary_color")?.textContent ||
          ""
      ),
      tertiary_color: JSON.parse(
        document.getElementById("institution_tertiary_color")?.textContent || ""
      ),
      depositInsurer: JSON.parse(
        document.getElementById("institution_deposit_insurer")?.textContent ||
          ""
      ),
      logo: JSON.parse(
        document.getElementById("institution_logo_url")?.textContent || ""
      ),
      apps: (rawInstitutionApps as string[][]).map((app) => App.deserialize(app)),
      servicesApps: (rawInstitutionServicesApps as string[][]).map((app) =>
        App.deserialize(app)
      ),
      fingerprintApiKey: JSON.parse(
        document.getElementById("institution_fingerprint_api_key")
          ?.textContent || ""
      ),
      fingerprintProxyDomain: JSON.parse(
        document.getElementById("institution_fingerprint_proxy_domain")
          ?.textContent || ""
      ),
    };
  }

  let features: InstitutionFeatures = {};
  if (document.getElementById("features")) {
    features = JSON.parse(
      document.getElementById("features")?.textContent || ""
    ) as InstitutionFeatures;
  }
  let organizationUsers: (OrganizationUser | null)[];
  if (document.getElementById("org_users")) {
    organizationUsers = JSON.parse(
      document.getElementById("org_users")?.textContent || ""
    );

    if (organizationUsers) {
      organizationUsers = organizationUsers.map((serialized) =>
        OrganizationUser.deserialize(serialized)
      );
    }
  }

  let organizationUser: OrganizationUser | null;
  if (document.getElementById("org_user")) {
    organizationUser = OrganizationUser.deserialize(
      JSON.parse(document.getElementById("org_user")?.textContent || "")
    );
  }

  let currentOrganizationUser: OrganizationUser | null;
  if (document.getElementById("current_org_user")) {
    currentOrganizationUser = OrganizationUser.deserialize(
      JSON.parse(document.getElementById("current_org_user")?.textContent || "")
    );
  }

  let approvalRequests: API.DualApproval[] = [];
  if (document.getElementById("approval_requests")) {
    approvalRequests = JSON.parse(
      document.getElementById("approval_requests")?.textContent || ""
    );
  }

  let supportContacts: SupportContact[] = [];
  if (document.getElementById("support_contacts")) {
    supportContacts = JSON.parse(
      document.getElementById("support_contacts")?.textContent || ""
    );
  }

  let supportFAQs: SupportFaqCategory[] = [];
  if (document.getElementById("support_faqs")) {
    supportFAQs = JSON.parse(
      document.getElementById("support_faqs")?.textContent || ""
    );
  }

  let supportLinks: SupportLink[] = [];
  if (document.getElementById("support_links")) {
    supportLinks = JSON.parse(
      document.getElementById("support_links")?.textContent || ""
    );
  }

  let currentUserUUID = "";
  if (document.getElementById("current_user_uuid")) {
    currentUserUUID = JSON.parse(
      document.getElementById("current_user_uuid")?.textContent || ""
    );
  }

  let currentUser: User | undefined;
  if (document.getElementById("current_user")) {
    const user: API.User = JSON.parse(
      document.getElementById("current_user")?.textContent || ""
    );
    dataToPreload.user = user;
    currentUser = User.deserialize(user);
  }

  /* eslint-disable no-underscore-dangle */
  if (currentUser && features?.olb_enable_agentiq && window.__aiq) {
    /* eslint-disable no-underscore-dangle */
    window.__aiq.use_sso = true;
    window.__aiq.renderChat();
  }

  let replyDisclaimer = "";
  if (document.getElementById("reply_disclaimer")) {
    replyDisclaimer = JSON.parse(
      document.getElementById("reply_disclaimer")?.textContent || ""
    );
  }

  let replyTime = "";
  if (document.getElementById("reply_time")) {
    replyTime = JSON.parse(
      document.getElementById("reply_time")?.textContent || ""
    );
  }

  let userMessageUploads = false;
  if (document.getElementById("user_message_uploads")) {
    userMessageUploads = JSON.parse(
      document.getElementById("user_message_uploads")?.textContent || ""
    );
  }

  if (document.getElementById("glia_user")) {
    const gliaUser = JSON.parse(
      document.getElementById("glia_user")?.textContent || ""
    );
    if (gliaUser) {
      initialiseGlia(gliaUser);
    }
  }

  let accounts: Account[] = [];
  if (document.getElementById("accounts")) {
    const accountArray = JSON.parse(
      document.getElementById("accounts")?.textContent || ""
    );
    if (accountArray.map) {
      dataToPreload.accounts = accountArray;
      accounts = accountArray.map((a: API.AnyAccount) =>
        Account.deserialize(a)
      );
    }
  }

  if (document.getElementById("banking_menus")) {
    dataToPreload.bankingMenus = JSON.parse(
      document.getElementById("banking_menus")?.textContent || "{}"
    );
  }

  dataToPreload.institutionApps = rawInstitutionApps;

  let externalAccounts: API.ExternalAccount[] = [];
  if (document.getElementById("external_accounts")) {
    const externalAccountArray = JSON.parse(
      document.getElementById("external_accounts")?.textContent || ""
    );
    if (externalAccountArray.map) {
      externalAccounts = externalAccountArray.map((a: API.ExternalAccount) =>
        Account.deserialize(a)
      );
    }
  }

  let memberships: Membership[] = [];

  if (document.getElementById("memberships")) {
    const membershipsArray = JSON.parse(
      document.getElementById("memberships")?.textContent || ""
    );
    if (membershipsArray.map) {
      memberships = membershipsArray.map((membershipId: string) =>
        Membership.deserialize(membershipId)
      );
    }
  }

  /* before we had NAF apps, we had service and sso urls */
  let legacyNafUrls: LegacyNafUrl[] = [];
  if (document.getElementById("service_urls")) {
    legacyNafUrls = JSON.parse(
      document.getElementById("service_urls")?.textContent || ""
    );
  }
  if (document.getElementById("sso_urls")) {
    legacyNafUrls = legacyNafUrls.concat(
      JSON.parse(document.getElementById("sso_urls")?.textContent || "")
    );
  }

  let deviceEnrolled = "";
  if (document.getElementById("device_enrolled")) {
    deviceEnrolled = JSON.parse(
      document.getElementById("device_enrolled")?.textContent || ""
    );
  }

  let institutionShortName = "";
  if (document.getElementById("institution_short_name")) {
    institutionShortName = JSON.parse(
      document.getElementById("institution_short_name")?.textContent || ""
    );
  }

  let otpDeviceChoices = "";
  if (document.getElementById("otp_device_choices")) {
    otpDeviceChoices = JSON.parse(
      document.getElementById("otp_device_choices")?.textContent || ""
    );
  }

  let deviceSelected = "";
  if (document.getElementById("device_selected")) {
    deviceSelected = JSON.parse(
      document.getElementById("device_selected")?.textContent || ""
    );
  }

  let challengeText = "";
  if (document.getElementById("challenge_text")) {
    challengeText = JSON.parse(
      document.getElementById("challenge_text")?.textContent || ""
    );
  }

  let recaptchaPublicKey = "";
  if (document.getElementById("recaptcha_public_key")) {
    recaptchaPublicKey = JSON.parse(
      document.getElementById("recaptcha_public_key")?.textContent || ""
    );
  }

  let deviceIsRemembered = false;
  if (document.getElementById("device_is_remembered")) {
    deviceIsRemembered = JSON.parse(
      document.getElementById("device_is_remembered")?.textContent || ""
    );
  }

  let announcement = "";
  if (document.getElementById("institution_announcement")) {
    announcement = JSON.parse(
      document.getElementById("institution_announcement")?.textContent || ""
    );
  }
  let storageId = "";
  if (document.getElementById("announcement_hash")) {
    storageId = JSON.parse(
      document.getElementById("announcement_hash")?.textContent || ""
    );
  }

  type ReactRootKeys =
    | "#InactivityDialog"
    | "#account-page-container"
    | "#add-a-card"
    | "#app-authorize"
    | "#app-download-dialog"
    | "#automatic-savings-container"
    | "#beneficiaries"
    | "#change-password-container"
    | "#disclosures-reconsent-dialog"
    | "#documents-page-container"
    | "#download-transactions-dialog"
    | "#dual-approval-notification-bar"
    | "#dual-approval-request-list"
    | "#enrollment-page-container"
    | "#estatements-manager"
    | "#footer-container"
    | "#login-page-container"
    | "#message-center-root"
    | "#mfa-code-enter"
    | "#mfa-device-select"
    | "#iframe-signed-url-page"
    | "#notification"
    | "#oauth2-authorize"
    | "#oauth2-authorize-error"
    | "#organization-edit-user"
    | "#organization-new-user"
    | "#organization-users"
    | "#password-reset-page-container"
    | "#positive-pay"
    | "#zescrow"
    | "#react-dashboard"
    | "#react-institution-announcement"
    | "#refer-a-member"
    | "#reports-root"
    | "#service-page-container"
    | "#settings-navbar"
    | "#sidebar-top-menu"
    | "#support-root"
    | "#top-menu"
    | "#transfer-page-container"
    | "#mobile-emulation";

  /**
   * Each entry in this record has an optional `isPage` boolean.
   * This property is used to determine if a root is a page component.
   */
  const reactRootComponents: Record<
    ReactRootKeys,
    {
      component: React.ComponentType<any>;
      isPage?: boolean;
      isHiddenWhileLocalizationLoading?: boolean;
    }
  > = {
    "#organization-users": {
      component: OrganizationUsersList,
      isPage: true,
    },
    "#organization-new-user": {
      component: OrganizationNewUser,
      isPage: true,
    },
    "#organization-edit-user": {
      component: OrganizationEditUser,
      isPage: true,
    },
    "#dual-approval-request-list": {
      component: DualApprovalContainer,
      isPage: true,
    },
    "#dual-approval-notification-bar": {
      component: DualApprovalNotificationBar,
    },
    "#estatements-manager": {
      component: EstatementsManager,
      isPage: true,
    },
    "#support-root": {
      component: SupportPageLayout,
      isPage: true,
    },
    "#message-center-root": {
      component: MessageCenterLayout,
      isPage: true,
    },
    "#top-menu": {
      component: Sidebar,
      isHiddenWhileLocalizationLoading: true,
    },
    "#sidebar-top-menu": {
      component: SidebarTop,
    },
    "#footer-container": {
      component: Footer,
    },
    "#notification": {
      component: withNotifications(null, true),
    },
    "#transfer-page-container": {
      component: TransferPageContainer,
      isPage: true,
    },
    "#react-dashboard": {
      component: Main,
      isPage: true,
    },
    "#react-institution-announcement": {
      component: InstitutionAnnouncement,
    },
    "#app-authorize": {
      component: AppAuthorize,
      isPage: true,
    },
    "#oauth2-authorize": {
      component: OAuth2Authorize,
      isPage: true,
    },
    "#oauth2-authorize-error": {
      component: OAuth2AuthorizeError,
      isPage: true,
    },
    "#refer-a-member": {
      component: ReferAMember,
      isPage: true,
    },
    "#beneficiaries": {
      component: BeneficiariesContainer,
      isPage: true,
    },
    "#login-page-container": {
      component: LoginPageContainer,
      isPage: true,
    },
    "#change-password-container": {
      component: ChangePasswordContainer,
      isPage: true,
    },
    "#enrollment-page-container": {
      component: EnrollmentPageContainer,
      isPage: true,
    },
    "#password-reset-page-container": {
      component: PasswordResetPageContainer,
      isPage: true,
    },
    "#positive-pay": {
      component: PositivePay,
      isPage: true,
    },
    "#zescrow": {
      component: ZEscrow,
      isPage: true,
    },
    "#InactivityDialog": {
      component: InactivityDialog,
    },
    "#mfa-device-select": {
      component: MfaDeviceSelect,
    },
    "#mfa-code-enter": {
      component: MfaCodeEnter,
    },
    "#account-page-container": {
      component: AccountPageContainer,
      isPage: true,
    },
    "#settings-navbar": {
      component: SettingsNavBarContainer,
      isPage: true,
    },
    "#documents-page-container": {
      component: DocumentsPageContainer,
      isPage: true,
    },
    "#automatic-savings-container": {
      component: AutomaticSavingsPageContainer,
      isPage: true,
    },
    "#service-page-container": {
      component: ServicePageContainer,
      isPage: true,
    },
    "#reports-root": {
      component: TransactionExportPageContainer,
      isPage: true,
    },
    "#add-a-card": {
      component: AddCardNafApp,
      isPage: true,
    },
    "#iframe-signed-url-page": {
      component: SignedUrlIframe,
      isPage: true,
    },
    "#disclosures-reconsent-dialog": {
      component: DisclosuresReconsentDialog,
    },
    "#app-download-dialog": {
      component: AppDownloadDialog,
    },
    "#download-transactions-dialog": {
      component: DownloadTransactionsDialog,
      isPage: true,
    },
    "#mobile-emulation": {
      component: MobileEmulation,
    },
  };

  Object.keys(reactRootComponents).forEach((key) => {
    const target = document.querySelector(key);
    if (target instanceof HTMLElement) {
      if (target?.dataset && "limitsJson" in target.dataset) {
        try {
          dataToPreload.limits = JSON.parse(target.dataset?.limitsJson || "");
        } catch (e) {
          console.warn("Failed to parse limitsJson");
        }
      }

      const { component: Component, isHiddenWhileLocalizationLoading } =
        reactRootComponents[key as ReactRootKeys];

      const element = React.createElement(Component, {
        ...target.dataset,
        approvalRequests,
        supportContacts,
        supportFAQs,
        supportLinks,
        currentOrganizationUser,
        organizationUser,
        organizationUsers,
        currentUser,
        currentUserUUID,
        replyDisclaimer,
        replyTime,
        features,
        institution,
        userMessageUploads,
        accounts,
        externalAccounts,
        memberships,
        legacyNafUrls,
        deviceEnrolled,
        institutionShortName,
        otpDeviceChoices,
        deviceSelected,
        challengeText,
        recaptchaPublicKey,
        deviceIsRemembered,
        announcement,
        storageId,
      });

      const root = ReactDOM.createRoot(target);
      const fingerprintEndpoint =
        institution.fingerprintProxyDomain || FingerprintJSPro.defaultEndpoint;
      const fingerprintScriptSrc = institution.fingerprintProxyDomain
        ? `${institution.fingerprintProxyDomain}/web/v<version>/<apiKey>/loader_v<loaderVersion>.js`
        : FingerprintJSPro.defaultScriptUrlPattern;
      root.render(
        <AppProviders
          internalName={institution.internalName || ""}
          s3ImagesBucket={institution.s3ImagesBucket || ""}
          fingerprintApiKey={institution.fingerprintApiKey || ""}
          fingerprintEndpoint={fingerprintEndpoint}
          fingerprintScriptSrc={fingerprintScriptSrc}
          deviceMonitoringEnabled={["decision", "report"].some(
            (mode) =>
              features.third_party_login_evaluations === mode ||
              features.third_party_ach_transaction_evaluations === mode ||
              features.third_party_wire_transaction_evaluations === mode ||
              features.third_party_fednow_transaction_evaluations === mode
          )}
          preloadedState={preloadState(dataToPreload)}
          currentUser={currentUser}
          isHiddenWhileLoading={isHiddenWhileLocalizationLoading}
        >
          {element}
        </AppProviders>
      );
    }
  });
});
